import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, groupsAPI } from '../services/api';

const CoordinatorForm = ({ isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    groupId: ''
  });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { id } = useParams();
  
  useEffect(() => {
    // Fetch all groups for the dropdown
    const fetchGroups = async () => {
      try {
        const response = await groupsAPI.getAllGroups();
        setGroups(response.data.data);
      } catch (err) {
        console.error('Error fetching groups:', err);
        setError('Failed to load groups');
      }
    };
    
    fetchGroups();
    
    // If editing, fetch coordinator data
    if (isEdit && id) {
      const fetchCoordinator = async () => {
        try {
          setLoading(true);
          const response = await adminAPI.getCoordinatorById(id);
          const coordinator = response.data.data;
          
          setFormData({
            name: coordinator.name || '',
            email: coordinator.email || '',
            password: '', // Don't populate password for security
            groupId: coordinator.groupId?._id || coordinator.groupId || ''
          });
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching coordinator:', err);
          setError('Failed to load coordinator data');
          setLoading(false);
        }
      };
      
      fetchCoordinator();
    }
  }, [isEdit, id]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Validate form
      if (!formData.name || !formData.email || (!isEdit && !formData.password) || !formData.groupId) {
        setError('All fields are required' + (isEdit ? ' (except password for editing)' : ''));
        setLoading(false);
        return;
      }
      
      let response;
      
      if (isEdit && id) {
        // Update existing coordinator
        const updateData = {
          name: formData.name,
          email: formData.email,
          groupId: formData.groupId
        };
        
        // Only include password if it's provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        response = await adminAPI.updateCoordinator(id, updateData);
        setSuccess('Coordinator updated successfully');
      } else {
        // Create new coordinator
        response = await adminAPI.createCoordinator(formData);
        setSuccess('Coordinator created successfully');
        
        // Reset form after successful creation
        setFormData({
          name: '',
          email: '',
          password: '',
          groupId: ''
        });
      }
      
      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate('/admin/coordinators');
      }, 1500);
      
    } catch (err) {
      console.error('Coordinator form error:', err);
      setError(
        err.response?.data?.message || 
        'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="mb-4">
      <Card.Header>
        <h4>{isEdit ? 'Edit Coordinator' : 'Add New Coordinator'}</h4>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter coordinator's name"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter coordinator's email"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>{isEdit ? 'New Password (leave blank to keep current)' : 'Password'}</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={isEdit ? "Enter new password (optional)" : "Enter password"}
              required={!isEdit}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Group</Form.Label>
            <Form.Select
              name="groupId"
              value={formData.groupId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Group --</option>
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.code})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <div className="d-flex justify-content-between mt-4">
            <Button 
              variant="secondary" 
              onClick={() => navigate('/admin/coordinators')}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Coordinator' : 'Create Coordinator')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CoordinatorForm;
